import { Steps } from "antd";
import { Formik } from "formik";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { FaWhatsapp } from "react-icons/fa";
import { FiPlus } from "react-icons/fi";
import { MdDeleteOutline, MdEmail } from "react-icons/md";
import ReactQuill from "react-quill-new";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Breadcrumbs from "../../components/Breadcrumbs";
import Button from "../../components/button";
import FileUpload from "../../components/FileUpload";
import Input from "../../components/input";
import PhoneNumberInput from "../../components/PhoneNumberInput";
import Select from "../../components/Select";
import {
  fetchBanksDropdown,
  fetchTransporterCitiesDropdown,
  fetchTransporterVehicleTypesDropdown,
} from "../../store/Slices/dropdownSlice";
import {
  fetchCustomerDetail,
  fetchCustomersPaginated,
} from "../../store/Slices/CustomerSlice";
import {
  createLoadBooking,
  fetchLoadBookingDetail,
} from "../../store/Slices/LoadBookingSlice";
import { fetchTransportersPaginated } from "../../store/Slices/TransporterSlice";
import "../../styles/LoadBooking.css";
import "../../styles/TransporterSteps.css";
import "../../styles/Trip.css";
import { formatUtcDateTimeForInput, formats, modules } from "../../utils/helper";
import { errorNotify, successNotify } from "../../utils/toast";
import {
  DRAFT_KEY,
  createGoods,
  defaultValues,
  billingFrequencyOptions,
  fallbackBankOptions,
  goodsTypeOptions,
  handlingUnitOptions,
  insuranceCompanyOptions,
  locationOptions,
  paymentModeOptions,
  paymentTermOptions,
  stepItems,
  stepSchemas,
  unitOptions,
  vehicleTypeOptions,
} from "./loadBooking/loadBooking.constants";
import {
  clearLoadBookingDraft,
  formToRow,
  generateId,
  loadBookings,
  normalize,
  prepareSnapshot,
  readStorage,
  safeArray,
  saveBookings,
  toCreateLoadPayload,
  toFormValues,
  touchedFromErrors,
  writeStorage,
} from "./loadBooking/loadBooking.helpers";

const toLabel = (item) =>
  String(item?.label ?? item?.name ?? item?.title ?? item?.city ?? item?.value ?? item ?? "").trim();
const toPositiveId = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};
const toText = (value) => String(value ?? "").trim();
const isSameRecordId = (record, targetId) =>
  String(record?.id ?? "") === String(targetId ?? "") ||
  String(record?.recordId ?? "") === String(targetId ?? "");
const toDateTimeLocal = (value) => {
  return formatUtcDateTimeForInput(value);
};

const resolveOptionValue = (options, value) => {
  const selectedId = toPositiveId(value);

  if (selectedId) {
    const matchedById = safeArray(options).find((option, index) => {
      const fallbackId = index + 1;
      const optionId = toPositiveId(option?.id ?? option?.value) || fallbackId;
      return optionId === selectedId;
    });

    if (matchedById) {
      return matchedById?.value ?? matchedById?.label ?? "";
    }
  }

  const selectedText = normalize(value);
  if (!selectedText) return "";

  const matchedByValue = safeArray(options).find(
    (option) =>
      normalize(option?.value) === selectedText ||
      normalize(option?.label) === selectedText ||
      normalize(option?.name) === selectedText ||
      normalize(option?.title) === selectedText
  );

  return matchedByValue?.value ?? value ?? "";
};

const resolveLoadStopValue = (stops, targetType) => {
  const matchedStop = safeArray(stops).find(
    (stop) => normalize(stop?.stop_type) === normalize(targetType)
  );
  if (!matchedStop) return "";

  const warehouseId = toPositiveId(
    matchedStop?.customer_warehouse_id ?? matchedStop?.warehouse_id
  );
  if (warehouseId) return warehouseId;

  return toText(
    matchedStop?.customer_warehouse?.warehouse_name ??
      matchedStop?.customer_warehouse?.name ??
      matchedStop?.warehouse?.warehouse_name ??
      matchedStop?.warehouse?.name ??
      matchedStop?.point ??
      matchedStop?.address
  );
};

const mapLoadBookingDetailToFormValues = (detail) => {
  const base = defaultValues();
  if (!detail || typeof detail !== "object") return base;

  const loadStops = safeArray(detail?.load_stops);
  const notify = safeArray(detail?.load_notify)[0] || {};
  const insuranceInfo = detail?.insurance_info || {};
  const uniqueGoods = new Map();

  loadStops.forEach((stop, index) => {
    const key = [
      stop?.good_id,
      stop?.qty_unit_id,
      stop?.qty_unit,
      stop?.weight_unit_id,
      stop?.weight,
      toText(stop?.hs_code),
      toText(stop?.goods_price),
    ].join("|");

    if (uniqueGoods.has(key)) return;

    uniqueGoods.set(key, {
      id: stop?.id || `goods-${index}`,
      goodType: resolveOptionValue(goodsTypeOptions, stop?.good_id),
      quantity: toText(stop?.qty_unit),
      handlingUnit: resolveOptionValue(handlingUnitOptions, stop?.qty_unit_id),
      hsCode: toText(stop?.hs_code),
      weight: toText(stop?.weight),
      unit: resolveOptionValue(unitOptions, stop?.weight_unit_id),
      price: toText(stop?.goods_price),
    });
  });

  const mappedGoods = Array.from(uniqueGoods.values());
  const customerId = toPositiveId(detail?.customer?.id ?? detail?.customer_id);
  const transporterId = toPositiveId(
    detail?.transporter?.id ?? detail?.transporter_id ?? notify?.transporter_id
  );
  const vehicleTypeSource =
    safeArray(detail?.types_of_vehicles)[0]?.id ??
    safeArray(detail?.types_of_vehicles)[0]?.title ??
    safeArray(detail?.vehicle_types)[0]?.id ??
    safeArray(detail?.vehicle_type_ids)[0];

  return {
    ...base,
    loadId: toText(detail?.load_id) || toText(detail?.id),
    customer: customerId || normalize(detail?.customer?.name),
    transporterName: toText(detail?.transporter?.name) || transporterId || "",
    vehicleType: vehicleTypeSource || "",
    loadingPoint: resolveLoadStopValue(loadStops, "loading"),
    offloadingPoint: resolveLoadStopValue(loadStops, "offloading"),
    departureDateTime: toDateTimeLocal(detail?.departure_at),
    deliveryDateTime: toDateTimeLocal(detail?.delivered_at),
    goods: mappedGoods.length
      ? mappedGoods.map((item) => ({
          ...createGoods(),
          ...item,
          id: item?.id || createGoods().id,
        }))
      : [createGoods()],
    isInsured: Boolean(detail?.insured),
    insurance: {
      ...base.insurance,
      company: resolveOptionValue(
        insuranceCompanyOptions,
        insuranceInfo?.insurance_company_id ?? detail?.insurance_company_id
      ),
      policyNumber: toText(
        insuranceInfo?.policy_number ?? detail?.insurance_policy_number
      ),
      coverageAmount: toText(insuranceInfo?.cover_amount ?? detail?.cover_amount),
      goodsPrice: toText(insuranceInfo?.good_price ?? detail?.insurance_good_price),
      contactNumber: toText(insuranceInfo?.contact ?? detail?.insurance_contact),
      email: toText(insuranceInfo?.email ?? detail?.insurance_email),
    },
    attachments: safeArray(detail?.files),
    payment: {
      ...base.payment,
      buyingPrice: toText(detail?.buying_price),
      sellingPrice: toText(detail?.selling_price),
      billingFrequency: toText(detail?.billing_frequency),
      paymentMode: toText(detail?.payment_method),
      paymentTerm: toText(detail?.payment_terms),
      bank: toText(detail?.bank_name),
      accountTitle: toText(detail?.account_title),
      iban: toText(detail?.iban_number),
    },
    notes: toText(detail?.notes ?? detail?.note) || base.notes,
    shareWith: {
      whatsapp: Boolean(notify?.whatsapp),
      email: Boolean(notify?.email),
    },
  };
};

const AddLoadBooking = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: routeLoadBookingId } = useParams();
  const dispatch = useDispatch();

  const { customerPaginatedData, customerPaginatedLoading } = useSelector(
    (state) => state.customer
  );
  const { transporterPaginatedData, transporterPaginatedLoading } = useSelector(
    (state) => state.transporter
  );
  const {
    banksDropdownData = [],
    transporterCitiesDropdownData = [],
    transporterVehicleTypesDropdownData = [],
    loading: dropdownLoading,
  } = useSelector((state) => state.dropdowns);
  const { createLoadBookingLoading, loadBookingDetailLoading } = useSelector(
    (state) => state.loadBooking
  );

  const isEditMode = Boolean(routeLoadBookingId) || location.state?.mode === "edit";
  const editRecordFromState = location.state?.record || null;
  const editRecordId =
    routeLoadBookingId ||
    location.state?.id ||
    editRecordFromState?.recordId ||
    editRecordFromState?.id ||
    null;

  const [currentStep, setCurrentStep] = useState(0);
  const [initialValues, setInitialValues] = useState(defaultValues());
  const [customerWarehouseOptions, setCustomerWarehouseOptions] = useState([]);
  const [customerWarehousesLoading, setCustomerWarehousesLoading] = useState(false);

  useEffect(() => {
    dispatch(
      fetchCustomersPaginated({
        page: 1,
        limit: 200,
        search_text: "",
        searchFilters: {},
      })
    );
    dispatch(
      fetchTransportersPaginated({
        page: 1,
        limit: 200,
        search_text: "",
        searchFilters: {},
      })
    );
    dispatch(fetchBanksDropdown());
    dispatch(fetchTransporterCitiesDropdown());
    dispatch(fetchTransporterVehicleTypesDropdown());
  }, [dispatch]);

  const customerOptions = useMemo(
    () =>
      safeArray(customerPaginatedData?.results)
        .map((item) => {
          const label = toLabel(item?.name);
          if (!label) return null;
          const recordId = toPositiveId(item?.id);
          return {
            label,
            value: recordId || normalize(label),
            recordId,
          };
        })
        .filter(Boolean),
    [customerPaginatedData]
  );

  const transporterOptions = useMemo(
    () =>
      safeArray(transporterPaginatedData?.results)
        .map((item) => {
          const label = toLabel(item?.name);
          if (!label) return null;
          const recordId = toPositiveId(item?.id);
          return {
            label,
            value: recordId || label,
            recordId,
          };
        })
        .filter(Boolean),
    [transporterPaginatedData]
  );

  const vehicleTypeDropdownOptions = useMemo(() => {
    const source = safeArray(transporterVehicleTypesDropdownData).length
      ? transporterVehicleTypesDropdownData
      : vehicleTypeOptions;

    return safeArray(source)
      .map((item) => {
        const label = toLabel(item);
        if (!label) return null;
        const rawValue = item?.value ?? item?.id ?? item?.vehicle_type_id;
        const recordId = toPositiveId(rawValue);
        return {
          label,
          value: recordId || rawValue || normalize(label),
          recordId,
        };
      })
      .filter(Boolean);
  }, [transporterVehicleTypesDropdownData]);

  const locationDropdownOptions = useMemo(() => {
    const source = safeArray(transporterCitiesDropdownData).length
      ? transporterCitiesDropdownData
      : locationOptions;

    return safeArray(source)
      .map((item) => {
        const label = toLabel(item);
        if (!label) return null;
        return {
          label,
          value: item?.value ?? item?.city ?? item?.name ?? label,
        };
      })
      .filter(Boolean);
  }, [transporterCitiesDropdownData]);

  const bankOptions = useMemo(
    () => (safeArray(banksDropdownData).length ? banksDropdownData : fallbackBankOptions),
    [banksDropdownData]
  );

  const matchOption = (options, selectedValue) => {
    const selected = String(selectedValue || "");
    return (
      safeArray(options).find(
        (option) =>
          normalize(option?.value) === normalize(selected) ||
          normalize(option?.label) === normalize(selected)
      ) || null
    );
  };

  const loadCustomerWarehouses = useCallback(async (customerValue) => {
    const selectedCustomer = matchOption(customerOptions, customerValue);
    const customerId = toPositiveId(
      selectedCustomer?.recordId ?? selectedCustomer?.value ?? customerValue
    );

    if (!customerId) {
      setCustomerWarehouseOptions([]);
      return;
    }

    setCustomerWarehousesLoading(true);
    try {
      const response = await dispatch(fetchCustomerDetail(customerId)).unwrap();
      const detail = response?.data || response || {};
      const mappedWarehouses = safeArray(detail?.warehouses)
        .map((warehouse) => {
          const warehouseId = toPositiveId(warehouse?.id);
          if (!warehouseId) return null;
          const name = toLabel(warehouse?.warehouse_name ?? warehouse?.name);
          const city = toLabel(warehouse?.city);
          return {
            label: [name, city].filter(Boolean).join(" - ") || `Warehouse ${warehouseId}`,
            value: warehouseId,
            recordId: warehouseId,
          };
        })
        .filter(Boolean);

      setCustomerWarehouseOptions(mappedWarehouses);
    } catch {
      setCustomerWarehouseOptions([]);
    } finally {
      setCustomerWarehousesLoading(false);
    }
  }, [customerOptions, dispatch]);

  useEffect(() => {
    if (initialValues?.customer) {
      loadCustomerWarehouses(initialValues.customer);
    } else {
      setCustomerWarehouseOptions([]);
    }
  }, [initialValues?.customer, customerOptions, loadCustomerWarehouses]);

  useEffect(() => {
    let ignore = false;

    const loadInitialValues = async () => {
      const currentRows = loadBookings();
      const recordFromList = safeArray(currentRows).find((item) =>
        isSameRecordId(item, editRecordId)
      );

      let nextValues =
        isEditMode && editRecordFromState
          ? toFormValues(editRecordFromState)
          : defaultValues();

      if (isEditMode && editRecordId) {
        if (recordFromList && !editRecordFromState) {
          nextValues = toFormValues(recordFromList);
        }

        try {
          const response = await dispatch(fetchLoadBookingDetail(editRecordId)).unwrap();
          const detail = response?.data || response || null;
          if (detail) {
            nextValues = mapLoadBookingDetailToFormValues(detail);
          }
        } catch (error) {
          if (!editRecordFromState && !recordFromList) {
            errorNotify(error || "Failed to fetch load booking detail");
          }
        }

        if (!nextValues.loadId) {
          nextValues.loadId = toText(recordFromList?.id) || toText(editRecordId);
        }
      } else {
        const draft = readStorage(DRAFT_KEY, null);
        if (draft) nextValues = toFormValues({ formData: draft });
        if (!nextValues.loadId) {
          nextValues.loadId = generateId(currentRows);
        }
      }

      const preparedGoods = safeArray(nextValues.goods).length
        ? safeArray(nextValues.goods).map((item) => ({
            ...createGoods(),
            ...item,
            id: item?.id || createGoods().id,
          }))
        : [createGoods()];

      nextValues.goods = preparedGoods;

      if (!ignore) {
        setInitialValues(nextValues);
        setCurrentStep(0);
      }
    };

    loadInitialValues();

    return () => {
      ignore = true;
    };
  }, [dispatch, isEditMode, editRecordFromState, editRecordId]);

  const pickOption = (options, selectedValue) => {
    return matchOption(options, selectedValue);
  };

  const findLabel = (options, selectedValue) =>
    pickOption(options, selectedValue)?.label || selectedValue || "-";

  const resolveSnapshotValue = (options, selectedValue) => {
    const label = findLabel(options, selectedValue);
    return label || "-";
  };

  const getFallbackTransporter = () =>
    transporterOptions[0]?.label || "";

  const handleSave = async (values, { setSubmitting }) => {
    try {
      const currentRows = loadBookings();
      const targetId = isEditMode
        ? values.loadId || editRecordId || generateId(currentRows)
        : values.loadId || generateId(currentRows);

      const existingRecord = currentRows.find((item) => isSameRecordId(item, targetId));
      const shouldAssignTransporter =
        values.shareWith?.email || values.shareWith?.whatsapp;
      const selectedTransporter =
        values.transporterName ||
        (shouldAssignTransporter ? getFallbackTransporter() : "");
      const stopPointOptions = customerWarehouseOptions.length
        ? customerWarehouseOptions
        : locationDropdownOptions;
      const selectedCustomerOption = pickOption(customerOptions, values.customer);
      const selectedTransporterOption = pickOption(
        transporterOptions,
        selectedTransporter
      );
      const transporterName =
        selectedTransporterOption?.label ||
        toText(selectedTransporter);
      const selectedVehicleTypeOption = pickOption(
        vehicleTypeDropdownOptions,
        values.vehicleType
      );
      const selectedLoadingOption = pickOption(stopPointOptions, values.loadingPoint);
      const selectedOffloadingOption = pickOption(
        stopPointOptions,
        values.offloadingPoint
      );

      const payloadValues = {
        ...values,
        customer: selectedCustomerOption?.value ?? values.customer,
        customerId: toPositiveId(
          selectedCustomerOption?.recordId ?? selectedCustomerOption?.value
        ),
        transporterId: toPositiveId(
          selectedTransporterOption?.recordId ?? selectedTransporterOption?.value
        ),
        vehicleType: selectedVehicleTypeOption?.value ?? values.vehicleType,
        loadingWarehouseId: toPositiveId(
          selectedLoadingOption?.recordId ?? selectedLoadingOption?.value
        ),
        offloadingWarehouseId: toPositiveId(
          selectedOffloadingOption?.recordId ?? selectedOffloadingOption?.value
        ),
        transporterName,
        customerName: findLabel(customerOptions, values.customer),
        loadingPointLabel: findLabel(stopPointOptions, values.loadingPoint),
        offloadingPointLabel: findLabel(stopPointOptions, values.offloadingPoint),
        expectedDeliveryDate: values.deliveryDateTime
          ? String(values.deliveryDateTime).split("T")[0]
          : "",
      };

      if (!isEditMode) {
        const apiPayload = toCreateLoadPayload(payloadValues);
        await dispatch(createLoadBooking(apiPayload)).unwrap();
      }

      const fallbackStatus =
        existingRecord?.status || (transporterName ? "active" : "inactive");
      const localRow = formToRow(payloadValues, targetId, fallbackStatus);
      const exists = currentRows.some((item) => isSameRecordId(item, targetId));
      const nextRows = exists
        ? currentRows.map((item) => (isSameRecordId(item, targetId) ? localRow : item))
        : [localRow, ...currentRows];

      saveBookings(nextRows);
      clearLoadBookingDraft();
      successNotify(
        isEditMode ? "Load booking updated successfully" : "Load created successfully"
      );
      navigate("/3pl/load-booking");
    } catch (error) {
      errorNotify(error || "Failed to save load booking");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={stepSchemas[currentStep]}
      enableReinitialize
      onSubmit={handleSave}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        setFieldTouched,
        validateForm,
        setTouched,
        isSubmitting,
      }) => {
        const isLastStep = currentStep === stepItems.length - 1;
        const deleteActionStyle = {
          border: "none",
          background: "transparent",
          color: "#EF0000",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: 0,
          fontSize: 15,
          fontWeight: 400,
          marginTop: 2,
        };
        const addActionBaseStyle = {
          width: "100%",
          borderRadius: 10,
          padding: "8px 14px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          fontSize: 13,
          fontWeight: 400,
          border: "1px solid #D3DEEE",
          background: "#F4F7FD",
          color: "#1447B1",
        };
        const sectionCardStyle = {
          background: "#FFFFFF",
          border: "1px solid #DDE3F0",
          borderRadius: 12,
          padding: "16px 14px",
          marginBottom: 14,
        };
        const sectionHeadingStyle = {
          background: "transparent",
          padding: 0,
          marginBottom: 12,
          color: "#3D3F5C",
          fontSize: 18,
          fontWeight: 600,
        };
        const requiredStar = <span style={{ color: "red", fontSize: 13 }}>*</span>;
        const requiredLabel = (text, isRequired = true) =>
          isRequired ? (
            <>
              {text} {requiredStar}
            </>
          ) : (
            text
          );
        const setDigitsOnly = (fieldName) => (event) => {
          const digits = String(event?.target?.value ?? "").replace(/[^\d.]/g, "");
          setFieldValue(fieldName, digits);
        };

        const nextStep = async () => {
          const allErrors = await validateForm();
          if (Object.keys(allErrors || {}).length) {
            setTouched(touchedFromErrors(allErrors));
            return;
          }
          setCurrentStep((previous) => Math.min(previous + 1, stepItems.length - 1));
        };

        const prevStep = () => setCurrentStep((previous) => Math.max(previous - 1, 0));

        const gotoStep = async (target) => {
          if (target <= currentStep) {
            setCurrentStep(target);
            return;
          }

          const allErrors = await validateForm();
          if (Object.keys(allErrors || {}).length) {
            setTouched(touchedFromErrors(allErrors));
            return;
          }
          setCurrentStep(target);
        };

        const saveDraft = () => {
          const currentRows = loadBookings();
          const targetId = isEditMode
            ? values.loadId || editRecordId || generateId(currentRows)
            : values.loadId || generateId(currentRows);
          const stopPointOptions = customerWarehouseOptions.length
            ? customerWarehouseOptions
            : locationDropdownOptions;
          const selectedCustomerOption = pickOption(customerOptions, values.customer);
          const selectedTransporterOption = pickOption(
            transporterOptions,
            values.transporterName
          );
          const transporterName =
            selectedTransporterOption?.label || toText(values.transporterName);
          const selectedVehicleTypeOption = pickOption(
            vehicleTypeDropdownOptions,
            values.vehicleType
          );
          const selectedLoadingOption = pickOption(stopPointOptions, values.loadingPoint);
          const selectedOffloadingOption = pickOption(
            stopPointOptions,
            values.offloadingPoint
          );

          const draftValues = {
            ...values,
            customer: selectedCustomerOption?.value ?? values.customer,
            customerId: toPositiveId(
              selectedCustomerOption?.recordId ?? selectedCustomerOption?.value
            ),
            transporterId: toPositiveId(
              selectedTransporterOption?.recordId ?? selectedTransporterOption?.value
            ),
            vehicleType: selectedVehicleTypeOption?.value ?? values.vehicleType,
            loadingWarehouseId: toPositiveId(
              selectedLoadingOption?.recordId ?? selectedLoadingOption?.value
            ),
            offloadingWarehouseId: toPositiveId(
              selectedOffloadingOption?.recordId ?? selectedOffloadingOption?.value
            ),
            transporterName,
            customerName: findLabel(customerOptions, values.customer),
            loadingPointLabel: findLabel(stopPointOptions, values.loadingPoint),
            offloadingPointLabel: findLabel(stopPointOptions, values.offloadingPoint),
            expectedDeliveryDate: values.deliveryDateTime
              ? String(values.deliveryDateTime).split("T")[0]
              : "",
          };
          const existingRecord = currentRows.find((item) =>
            isSameRecordId(item, targetId)
          );
          const payload = formToRow(
            draftValues,
            targetId,
            existingRecord?.status || "inactive"
          );

          const exists = currentRows.some((item) => isSameRecordId(item, targetId));
          const nextRows = exists
            ? currentRows.map((item) => (isSameRecordId(item, targetId) ? payload : item))
            : [payload, ...currentRows];

          saveBookings(nextRows);
          writeStorage(DRAFT_KEY, { ...draftValues, loadId: targetId });
          if (!values.loadId) {
            setFieldValue("loadId", targetId);
          }
          successNotify("Draft saved successfully");
        };

        const addGoodsRow = () => {
          setFieldValue("goods", [...safeArray(values.goods), createGoods()]);
        };

        const removeGoodsRow = (id) => {
          const nextRows = safeArray(values.goods).filter((item) => item.id !== id);
          setFieldValue("goods", nextRows.length ? nextRows : [createGoods()]);
        };

        const snapshot = prepareSnapshot(values);
        const transporterName = values.transporterName || getFallbackTransporter();
        const stopPointOptions = customerWarehouseOptions.length
          ? customerWarehouseOptions
          : locationDropdownOptions;

        return (
          <>
            <Breadcrumbs
              values={["Load Booking", isEditMode ? "Edit Load" : "Create Load"]}
            />
            <div className="page_header access_view">
              <h2>{isEditMode ? "Edit Load" : "Create Load"}</h2>
              <div className="d-flex align-items-center gap-2">
                {!isEditMode && (
                  <button
                    type="button"
                    className="load_booking_draft_btn"
                    onClick={saveDraft}
                  >
                    Save As Draft
                  </button>
                )}
                <Button
                  cancelBtn={() => navigate("/3pl/load-booking")}
                  shouldRow={false}
                  isShowSubmitBtn={false}
                  cancelBtnText="Cancel"
                />
                <Button
                  submitText="Save"
                  submitBtn={handleSubmit}
                  loading={
                    isSubmitting || createLoadBookingLoading || loadBookingDetailLoading
                  }
                  disabled={
                    (!isEditMode && !isLastStep) ||
                    isSubmitting ||
                    createLoadBookingLoading ||
                    loadBookingDetailLoading
                  }
                  shouldRow={false}
                  isShowCancel={false}
                />
              </div>
            </div>

            <div className="page_container">
              <Row>
                <Col md={9}>
                  <div className="trip_information_main_div">
                    <h5>Load Details</h5>
                    <div>
                      <h6>
                        Load ID: <span>{values.loadId || "--"}</span>
                      </h6>
                      <div className="d-flex">
                        {!isEditMode && (
                          <div style={{ minWidth: "175px" }}>
                            <Steps
                              className="transporter_steps load_booking_steps form_steps_vertical"
                              current={currentStep}
                              direction="vertical"
                              items={stepItems}
                              onChange={gotoStep}
                            />
                          </div>
                        )}

                        <div className="w-100 mb-3">
                          {(isEditMode || currentStep === 0) && (
                            <div style={isEditMode ? sectionCardStyle : undefined}>
                              {isEditMode && (
                                <h6 style={sectionHeadingStyle}>Load Info</h6>
                              )}
                              <Row>
                                <Col md={6}>
                                  <Select
                                    label={requiredLabel("Customer")}
                                    placeholder="Select Customer"
                                    options={customerOptions}
                                    loading={customerPaginatedLoading}
                                    value={pickOption(customerOptions, values.customer)}
                                    name="customer"
                                    onChange={(option) => {
                                      const nextValue = option ? option.value : "";
                                      setFieldValue("customer", nextValue);
                                      setFieldValue("loadingPoint", "");
                                      setFieldValue("offloadingPoint", "");
                                      loadCustomerWarehouses(nextValue);
                                    }}
                                    onBlur={() => setFieldTouched("customer", true)}
                                    error={errors.customer}
                                    touched={touched.customer}
                                  />
                                </Col>
                                <Col md={6}>
                                  <Select
                                    label={requiredLabel("Vehicle Type")}
                                    placeholder="Select Vehicle Type"
                                    options={vehicleTypeDropdownOptions}
                                    loading={dropdownLoading}
                                    value={pickOption(
                                      vehicleTypeDropdownOptions,
                                      values.vehicleType
                                    )}
                                    name="vehicleType"
                                    onChange={(option) =>
                                      setFieldValue("vehicleType", option ? option.value : "")
                                    }
                                    onBlur={() => setFieldTouched("vehicleType", true)}
                                    error={errors.vehicleType}
                                    touched={touched.vehicleType}
                                  />
                                </Col>
                                <Col md={6}>
                                  <Select
                                    label={requiredLabel("Loading Point")}
                                    placeholder="Select Location"
                                    options={stopPointOptions}
                                    loading={dropdownLoading || customerWarehousesLoading}
                                    value={pickOption(
                                      stopPointOptions,
                                      values.loadingPoint
                                    )}
                                    name="loadingPoint"
                                    onChange={(option) =>
                                      setFieldValue("loadingPoint", option ? option.value : "")
                                    }
                                    onBlur={() => setFieldTouched("loadingPoint", true)}
                                    error={errors.loadingPoint}
                                    touched={touched.loadingPoint}
                                  />
                                </Col>
                                <Col md={6}>
                                  <Select
                                    label={requiredLabel("Offloading Point")}
                                    placeholder="Select Location"
                                    options={stopPointOptions}
                                    loading={dropdownLoading || customerWarehousesLoading}
                                    value={pickOption(
                                      stopPointOptions,
                                      values.offloadingPoint
                                    )}
                                    name="offloadingPoint"
                                    onChange={(option) =>
                                      setFieldValue("offloadingPoint", option ? option.value : "")
                                    }
                                    onBlur={() => setFieldTouched("offloadingPoint", true)}
                                    error={errors.offloadingPoint}
                                    touched={touched.offloadingPoint}
                                  />
                                </Col>
                                <Col md={6}>
                                  <Input
                                    label="Departure Date & Time"
                                    placeholder="Select Date & Time"
                                    type="datetime-local"
                                    name="departureDateTime"
                                    value={values.departureDateTime}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.departureDateTime}
                                    touched={touched.departureDateTime}
                                  />
                                </Col>
                                <Col md={6}>
                                  <Input
                                    label="Delivery Date & Time"
                                    placeholder="Select Date & Time"
                                    type="datetime-local"
                                    name="deliveryDateTime"
                                    value={values.deliveryDateTime}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.deliveryDateTime}
                                    touched={touched.deliveryDateTime}
                                  />
                                </Col>
                              </Row>

                              {!isEditMode && (
                                <div className="d-flex align-items-center gap-2 mt-2">
                                  <button type="button" className="cancel_btn" disabled>
                                    Previous
                                  </button>
                                  <button type="button" className="main_btn" onClick={nextStep}>
                                    Next
                                  </button>
                                </div>
                              )}
                            </div>
                          )}

                          {(isEditMode || currentStep === 1) && (
                            <div style={isEditMode ? sectionCardStyle : undefined}>
                              {isEditMode && (
                                <h6 style={sectionHeadingStyle}>Goods Details</h6>
                              )}
                              {safeArray(values.goods).map((item, index) => (
                                <div key={item.id}>
                                  <Row>
                                    <Col md={6}>
                                      <Select
                                        label={requiredLabel("Good Type")}
                                        placeholder="Select Good Type"
                                        options={goodsTypeOptions}
                                        value={pickOption(goodsTypeOptions, item.goodType)}
                                        name={`goods[${index}].goodType`}
                                        onChange={(option) =>
                                          setFieldValue(
                                            `goods[${index}].goodType`,
                                            option ? option.value : ""
                                          )
                                        }
                                        onBlur={() =>
                                          setFieldTouched(`goods[${index}].goodType`, true)
                                        }
                                        error={errors.goods?.[index]?.goodType}
                                        touched={touched.goods?.[index]?.goodType}
                                      />
                                    </Col>
                                    <Col md={3}>
                                      <Input
                                        label="Quantity"
                                        placeholder="00"
                                        name={`goods[${index}].quantity`}
                                        value={item.quantity}
                                        onChange={setDigitsOnly(`goods[${index}].quantity`)}
                                        onBlur={handleBlur}
                                        error={errors.goods?.[index]?.quantity}
                                        touched={touched.goods?.[index]?.quantity}
                                      />
                                    </Col>
                                    <Col md={3}>
                                      <Select
                                        label={requiredLabel("Handling Unit")}
                                        placeholder="Select Unit"
                                        options={handlingUnitOptions}
                                        value={pickOption(
                                          handlingUnitOptions,
                                          item.handlingUnit
                                        )}
                                        name={`goods[${index}].handlingUnit`}
                                        onChange={(option) =>
                                          setFieldValue(
                                            `goods[${index}].handlingUnit`,
                                            option ? option.value : ""
                                          )
                                        }
                                        onBlur={() =>
                                          setFieldTouched(`goods[${index}].handlingUnit`, true)
                                        }
                                        error={errors.goods?.[index]?.handlingUnit}
                                        touched={touched.goods?.[index]?.handlingUnit}
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <Input
                                        label="Goods HS Code"
                                        placeholder="Enter HS Code"
                                        name={`goods[${index}].hsCode`}
                                        value={item.hsCode}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.goods?.[index]?.hsCode}
                                        touched={touched.goods?.[index]?.hsCode}
                                      />
                                    </Col>
                                    <Col md={3}>
                                      <Input
                                        label="Weight"
                                        placeholder="00"
                                        name={`goods[${index}].weight`}
                                        value={item.weight}
                                        onChange={setDigitsOnly(`goods[${index}].weight`)}
                                        onBlur={handleBlur}
                                        error={errors.goods?.[index]?.weight}
                                        touched={touched.goods?.[index]?.weight}
                                      />
                                    </Col>
                                    <Col md={3}>
                                      <Select
                                        label={requiredLabel("Unit")}
                                        placeholder="Select Unit"
                                        options={unitOptions}
                                        value={pickOption(unitOptions, item.unit)}
                                        name={`goods[${index}].unit`}
                                        onChange={(option) =>
                                          setFieldValue(
                                            `goods[${index}].unit`,
                                            option ? option.value : ""
                                          )
                                        }
                                        onBlur={() =>
                                          setFieldTouched(`goods[${index}].unit`, true)
                                        }
                                        error={errors.goods?.[index]?.unit}
                                        touched={touched.goods?.[index]?.unit}
                                      />
                                    </Col>
                                  </Row>

                                  <button
                                    type="button"
                                    style={deleteActionStyle}
                                    onClick={() => removeGoodsRow(item.id)}
                                  >
                                    <MdDeleteOutline style={{ fontSize: 18 }} />
                                    Delete Goods
                                  </button>

                                  <div className="load_booking_divider" />
                                </div>
                              ))}

                              <button
                                type="button"
                                style={addActionBaseStyle}
                                onClick={addGoodsRow}
                              >
                                <FiPlus style={{ fontSize: 20 }} />
                                Add Goods
                              </button>

                              {touched.goods && typeof errors.goods === "string" && (
                                <div className="error-message load_booking_error_message">
                                  {errors.goods}
                                </div>
                              )}

                              <div className="load_booking_divider" />
                              <h6 className="load_booking_sub_heading">Insurance</h6>

                              <label className="load_booking_checkbox_label">
                                <input
                                  type="checkbox"
                                  checked={Boolean(values.isInsured)}
                                  onChange={(event) =>
                                    setFieldValue("isInsured", event.target.checked)
                                  }
                                />
                                Are the goods insured against loss or damage during transit?
                              </label>

                              {values.isInsured && (
                                <Row className="mt-2">
                                  <Col md={6}>
                                    <Select
                                      label={requiredLabel("Insurance Company")}
                                      placeholder="Select Company"
                                      options={insuranceCompanyOptions}
                                      value={pickOption(
                                        insuranceCompanyOptions,
                                        values.insurance.company
                                      )}
                                      name="insurance.company"
                                      onChange={(option) =>
                                        setFieldValue(
                                          "insurance.company",
                                          option ? option.value : ""
                                        )
                                      }
                                      onBlur={() =>
                                        setFieldTouched("insurance.company", true)
                                      }
                                      error={errors.insurance?.company}
                                      touched={touched.insurance?.company}
                                    />
                                  </Col>
                                  <Col md={6}>
                                    <Input
                                      label="Policy / Reference Number"
                                      placeholder="Enter Policy Number"
                                      name="insurance.policyNumber"
                                      value={values.insurance.policyNumber}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      error={errors.insurance?.policyNumber}
                                      touched={touched.insurance?.policyNumber}
                                    />
                                  </Col>
                                  <Col md={6}>
                                    <Input
                                      label="Coverage Amount (PKR)"
                                      placeholder="000"
                                      name="insurance.coverageAmount"
                                      value={values.insurance.coverageAmount}
                                      onChange={setDigitsOnly("insurance.coverageAmount")}
                                      onBlur={handleBlur}
                                      error={errors.insurance?.coverageAmount}
                                      touched={touched.insurance?.coverageAmount}
                                    />
                                  </Col>
                                  <Col md={6}>
                                    <Input
                                      label="Goods Price (PKR)"
                                      placeholder="000"
                                      name="insurance.goodsPrice"
                                      value={values.insurance.goodsPrice}
                                      onChange={setDigitsOnly("insurance.goodsPrice")}
                                      onBlur={handleBlur}
                                      error={errors.insurance?.goodsPrice}
                                      touched={touched.insurance?.goodsPrice}
                                    />
                                  </Col>
                                  <Col md={6}>
                                    <PhoneNumberInput
                                      label="Contact Number"
                                      name="insurance.contactNumber"
                                      value={values.insurance.contactNumber}
                                      setFieldValue={setFieldValue}
                                      onBlur={handleBlur}
                                      error={errors.insurance?.contactNumber}
                                      touched={touched.insurance?.contactNumber}
                                    />
                                  </Col>
                                  <Col md={6}>
                                    <Input
                                      label="Email Address"
                                      placeholder="Enter Email"
                                      name="insurance.email"
                                      value={values.insurance.email}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      error={errors.insurance?.email}
                                      touched={touched.insurance?.email}
                                    />
                                  </Col>
                                </Row>
                              )}

                              {!isEditMode && (
                                <div className="d-flex align-items-center gap-2 mt-2">
                                  <button
                                    type="button"
                                    className="cancel_btn"
                                    onClick={prevStep}
                                  >
                                    Previous
                                  </button>
                                  <button type="button" className="main_btn" onClick={nextStep}>
                                    Next
                                  </button>
                                </div>
                              )}
                            </div>
                          )}

                          {(isEditMode || currentStep === 2) && (
                            <div style={isEditMode ? sectionCardStyle : undefined}>
                              {isEditMode && (
                                <h6 style={sectionHeadingStyle}>Attachments</h6>
                              )}
                              <p className="load_booking_upload_note">
                                You can attach up to 5 files, max 1MB each.
                              </p>

                              <FileUpload
                                destination="3pl-load-booking"
                                files={safeArray(values.attachments)}
                                setFiles={(nextFiles) => {
                                  const resolved =
                                    typeof nextFiles === "function"
                                      ? nextFiles(safeArray(values.attachments))
                                      : nextFiles;
                                  setFieldValue("attachments", resolved);
                                }}
                                maxFiles={5}
                                maxFileSize={1}
                              />

                              {touched.attachments && errors.attachments && (
                                <div className="error-message load_booking_error_message">
                                  {errors.attachments}
                                </div>
                              )}

                              {!isEditMode && (
                                <div className="d-flex align-items-center gap-2 mt-3">
                                  <button
                                    type="button"
                                    className="cancel_btn"
                                    onClick={prevStep}
                                  >
                                    Previous
                                  </button>
                                  <button type="button" className="main_btn" onClick={nextStep}>
                                    Next
                                  </button>
                                </div>
                              )}
                            </div>
                          )}

                          {(isEditMode || currentStep === 3) && (
                            <div style={isEditMode ? sectionCardStyle : undefined}>
                              {isEditMode && (
                                <h6 style={sectionHeadingStyle}>Payment Info</h6>
                              )}
                              <Row>
                                <Col md={6}>
                                  <Input
                                    label="Buying Price"
                                    placeholder="Enter Buying Price"
                                    name="payment.buyingPrice"
                                    value={values.payment.buyingPrice}
                                    onChange={setDigitsOnly("payment.buyingPrice")}
                                    onBlur={handleBlur}
                                    error={errors.payment?.buyingPrice}
                                    touched={touched.payment?.buyingPrice}
                                  />
                                </Col>
                                <Col md={6}>
                                  <Input
                                    label="Selling Price"
                                    placeholder="Enter Selling Price"
                                    name="payment.sellingPrice"
                                    value={values.payment.sellingPrice}
                                    onChange={setDigitsOnly("payment.sellingPrice")}
                                    onBlur={handleBlur}
                                    error={errors.payment?.sellingPrice}
                                    touched={touched.payment?.sellingPrice}
                                  />
                                </Col>
                                <Col md={6}>
                                  <Select
                                    label={requiredLabel("Billing Frequency")}
                                    placeholder="Select Frequency"
                                    options={billingFrequencyOptions}
                                    value={pickOption(
                                      billingFrequencyOptions,
                                      values.payment.billingFrequency
                                    )}
                                    name="payment.billingFrequency"
                                    onChange={(option) =>
                                      setFieldValue(
                                        "payment.billingFrequency",
                                        option ? option.value : ""
                                      )
                                    }
                                    onBlur={() =>
                                      setFieldTouched("payment.billingFrequency", true)
                                    }
                                    error={errors.payment?.billingFrequency}
                                    touched={touched.payment?.billingFrequency}
                                  />
                                </Col>
                                <Col md={6}>
                                  <Select
                                    label={requiredLabel("Payment Mode")}
                                    placeholder="Select Mode"
                                    options={paymentModeOptions}
                                    value={pickOption(
                                      paymentModeOptions,
                                      values.payment.paymentMode
                                    )}
                                    name="payment.paymentMode"
                                    onChange={(option) => {
                                      const mode = option ? option.value : "";
                                      setFieldValue("payment.paymentMode", mode);

                                      if (mode !== "bank_transfer") {
                                        setFieldValue("payment.bank", "");
                                        setFieldValue("payment.accountTitle", "");
                                        setFieldValue("payment.iban", "");
                                      }
                                    }}
                                    onBlur={() =>
                                      setFieldTouched("payment.paymentMode", true)
                                    }
                                    error={errors.payment?.paymentMode}
                                    touched={touched.payment?.paymentMode}
                                  />
                                </Col>
                                <Col md={6}>
                                  <Select
                                    label={requiredLabel("Payment Term")}
                                    placeholder="Select Term"
                                    options={paymentTermOptions}
                                    value={pickOption(
                                      paymentTermOptions,
                                      values.payment.paymentTerm
                                    )}
                                    name="payment.paymentTerm"
                                    onChange={(option) =>
                                      setFieldValue(
                                        "payment.paymentTerm",
                                        option ? option.value : ""
                                      )
                                    }
                                    onBlur={() =>
                                      setFieldTouched("payment.paymentTerm", true)
                                    }
                                    error={errors.payment?.paymentTerm}
                                    touched={touched.payment?.paymentTerm}
                                  />
                                </Col>
                                {values.payment.paymentMode === "bank_transfer" && (
                                  <>
                                    <Col md={6}>
                                      <Select
                                        label={requiredLabel("Bank")}
                                        placeholder="Select Bank"
                                        options={bankOptions}
                                        loading={dropdownLoading}
                                        value={pickOption(bankOptions, values.payment.bank)}
                                        name="payment.bank"
                                        onChange={(option) =>
                                          setFieldValue(
                                            "payment.bank",
                                            option ? option.value : ""
                                          )
                                        }
                                        onBlur={() => setFieldTouched("payment.bank", true)}
                                        error={errors.payment?.bank}
                                        touched={touched.payment?.bank}
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <Input
                                        label="Account Title"
                                        placeholder="Enter Account Title"
                                        name="payment.accountTitle"
                                        value={values.payment.accountTitle}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.payment?.accountTitle}
                                        touched={touched.payment?.accountTitle}
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <Input
                                        label="IBAN / Account Number"
                                        placeholder="Enter IBAN / Account Number"
                                        name="payment.iban"
                                        value={values.payment.iban}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.payment?.iban}
                                        touched={touched.payment?.iban}
                                      />
                                    </Col>
                                  </>
                                )}
                              </Row>

                              {!isEditMode && (
                                <div className="d-flex align-items-center gap-2 mt-2">
                                  <button
                                    type="button"
                                    className="cancel_btn"
                                    onClick={prevStep}
                                  >
                                    Previous
                                  </button>
                                  <button type="button" className="main_btn" onClick={nextStep}>
                                    Next
                                  </button>
                                </div>
                              )}
                            </div>
                          )}

                          {(isEditMode || currentStep === 4) && (
                            <div style={isEditMode ? sectionCardStyle : undefined}>
                              {isEditMode && (
                                <h6 style={sectionHeadingStyle}>Snapshot</h6>
                              )}
                              <div className="load_snapshot_card">
                              <h4>Load Booking Snapshot</h4>
                              <p>
                                Review load information including vehicle, route, and
                                schedule details.
                              </p>

                              <h6>Goods Details</h6>
                              <div className="load_snapshot_table_wrap">
                                <table className="load_snapshot_table">
                                  <thead>
                                    <tr>
                                      <th>Good Type</th>
                                      <th>Quantity</th>
                                      <th>Weight</th>
                                      <th>HS Code</th>
                                      <th>Price</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {snapshot.goods.map((item, index) => (
                                      <tr key={`${item.hsCode}-${index}`}>
                                        <td>
                                          {resolveSnapshotValue(
                                            goodsTypeOptions,
                                            item.goodType
                                          )}
                                        </td>
                                        <td>
                                          {item.quantity || 0} (
                                          {resolveSnapshotValue(
                                            handlingUnitOptions,
                                            item.handlingUnit
                                          )}
                                          )
                                        </td>
                                        <td>
                                          {item.weight || 0}{" "}
                                          {resolveSnapshotValue(unitOptions, item.unit)}
                                        </td>
                                        <td>{item.hsCode || "-"}</td>
                                        <td>{item.price || values.payment.sellingPrice || "-"}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>

                              <h6>Other Information</h6>
                              <div className="load_snapshot_info">
                                <div>
                                  <span>Vehicle Required</span>
                                  <span>
                                    {resolveSnapshotValue(
                                      vehicleTypeDropdownOptions,
                                      values.vehicleType
                                    )}
                                  </span>
                                </div>
                                <div>
                                  <span>Loading Point</span>
                                  <span>
                                    {resolveSnapshotValue(
                                      stopPointOptions,
                                      values.loadingPoint
                                    )}
                                  </span>
                                </div>
                                <div>
                                  <span>Offloading Point</span>
                                  <span>
                                    {resolveSnapshotValue(
                                      stopPointOptions,
                                      values.offloadingPoint
                                    )}
                                  </span>
                                </div>
                                <div>
                                  <span>Departure</span>
                                  <span>{values.departureDateTime || "-"}</span>
                                </div>
                                <div>
                                  <span>Delivery</span>
                                  <span>{values.deliveryDateTime || "-"}</span>
                                </div>
                              </div>

                              <h6>Comments</h6>
                              <div className="load_snapshot_comment">{snapshot.notes || "-"}</div>

                                {!isEditMode && (
                                  <div className="d-flex align-items-center gap-2 mt-3">
                                    <button
                                      type="button"
                                      className="cancel_btn"
                                      onClick={prevStep}
                                    >
                                      Previous
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>

                <Col md={3}>
                  {currentStep === 4 ? (
                    <div
                      className="trip_notes_main_div load_booking_share_panel"
                      style={{ minHeight: "80vh", height: "100%" }}
                    >
                      <h5>Share Details With Transporter</h5>
                      <div className="load_booking_share_body">
                        <p>
                          Choose a method to send load details to the selected
                          transporter.
                        </p>
                        <Select
                          label="Transporter"
                          placeholder="Select Transporter"
                          options={transporterOptions}
                          loading={transporterPaginatedLoading}
                          value={pickOption(transporterOptions, values.transporterName)}
                          name="transporterName"
                          onChange={(option) =>
                            setFieldValue("transporterName", option ? option.label : "")
                          }
                          onBlur={() => setFieldTouched("transporterName", true)}
                        />
                        <div className="load_booking_share_table_head">
                          <span>Transporter Name</span>
                          <span>
                            <FaWhatsapp color="#25D366" size={30} />
                          </span>
                          <span>
                            <MdEmail color="#4A84D8" size={28} />
                          </span>
                        </div>
                        <div className="load_booking_share_table_row">
                          <span className="hyperlink">{transporterName}</span>
                          <span>
                            <input
                              type="checkbox"
                              checked={Boolean(values.shareWith?.whatsapp)}
                              onChange={(event) => {
                                setFieldValue(
                                  "shareWith.whatsapp",
                                  event.target.checked
                                );
                                if (event.target.checked && !values.transporterName) {
                                  setFieldValue("transporterName", transporterName);
                                }
                              }}
                            />
                          </span>
                          <span>
                            <input
                              type="checkbox"
                              checked={Boolean(values.shareWith?.email)}
                              onChange={(event) => {
                                setFieldValue("shareWith.email", event.target.checked);
                                if (event.target.checked && !values.transporterName) {
                                  setFieldValue("transporterName", transporterName);
                                }
                              }}
                            />
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="trip_notes_main_div"
                      style={{ minHeight: "80vh", height: "100%" }}
                    >
                      <h5>Comments/ Notes</h5>
                      <div style={{ padding: "0 12px 12px" }}>
                        <ReactQuill
                          placeholder="Type your notes or any additional info..."
                          modules={modules}
                          theme="snow"
                          value={values.notes || ""}
                          formats={formats}
                          onChange={(content) => setFieldValue("notes", content)}
                          className="react_quill_input notes_quill_input quill-small-font trip_quill_input"
                        />
                      </div>
                    </div>
                  )}
                </Col>
              </Row>
            </div>
          </>
        );
      }}
    </Formik>
  );
};

export default AddLoadBooking;
